import { useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Download, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { downloadRtdChecklist } from "@/lib/api";

const BLUE = "#0E509C";
const NAVY = "#10346E";
const ORANGE = "#E0663F";

const GROUPS = [
  { key: "records", title: "Records that move", count: 10, preview: ["Personal-trading history & pre-clearance log", "Quarterly transaction & annual holdings reports", "Form ADV / CRS version history", "Compliance manual & policies", "Marketing review log · vendor DD files · exam correspondence"] },
  { key: "exports", title: "Data to export from the current vendor", count: 5, preview: ["Employee roster, roles & access-person status", "Brokerage account feeds & restricted lists", "Open tasks, calendar & audit logs"] },
  { key: "cutover", title: "Access & cutover", count: 5, preview: ["Custodian / brokerage feed re-authorizations", "SSO & user provisioning", "Parallel-run dates · final export · termination notice before 12/31"] },
  { key: "rebuild", title: "What we rebuild better", count: 4, preview: ["Adviser-specific risk assessment", "Living regulatory calendar", "401(k) plan-committee tracker · SEC exam production index"] },
];

export function MigrationChecklist() {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      await downloadRtdChecklist();
      toast.success("Checklist downloaded — hand it to your team.");
    } catch (e) {
      toast.error("Couldn't build the PDF — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl p-5 md:p-6" data-testid="migration-checklist"
         style={{ background: NAVY, color: "#fff" }}>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="h-11 w-11 rounded-xl grid place-items-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.12)" }}>
          <ClipboardList className="h-5 w-5" style={{ color: "#FFB08A" }} strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.16em]" style={{ color: "#4F86D6" }}>Migration checklist · 24 items · PDF</div>
          <div className="rt-h text-lg mt-0.5">What moves over from your current vendor</div>
          <p className="text-[13px] leading-relaxed mt-1" style={{ opacity: 0.78 }}>
            A one-page, RTD-branded checklist with tick boxes and owner / target-date columns — hand it to your team
            and start requesting exports now. Your dedicated expert works the same list with you during the parallel run.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="checklist-preview-toggle"
                  className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-[13px] font-bold border"
                  style={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff" }}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {open ? "Hide" : "Preview"}
          </button>
          <button type="button" onClick={download} disabled={busy} data-testid="checklist-download"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-[13px] font-extrabold disabled:opacity-60 transition-transform hover:-translate-y-0.5"
                  style={{ background: ORANGE, color: "#fff" }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {busy ? "Building…" : "Download PDF"}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="checklist-preview">
          {GROUPS.map((g, i) => (
            <div key={g.key} className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid={`checklist-group-${g.key}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="rt-h text-[14px]">{i + 1} · {g.title}</div>
                <span className="text-[11px] font-bold" style={{ color: "#4F86D6" }}>{g.count}</span>
              </div>
              <ul className="mt-2 space-y-1.5">
                {g.preview.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[12px] leading-snug" style={{ opacity: 0.82 }}>
                    <span className="mt-1 h-3 w-3 rounded-[3px] border flex-shrink-0" style={{ borderColor: BLUE, background: "rgba(255,255,255,0.9)" }} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

